import { useRef, useState } from 'react'

import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import {
  type ImportSupply48DataResponse,
  ImportSupply48DataRowStatus,
} from '@ebalance/proto/bffuser/v1/supply_48_data_service_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Alert,
  Box,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
  toaster as rawToaster,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CsvDiffLoading } from '~/components/common/CsvUpload/CsvDiffLoading'
import { CsvDiffPanel } from '~/components/common/CsvUpload/CsvDiffPanel'
import { CsvUploadFileInfo } from '~/components/common/CsvUpload/CsvUploadFileInfo'
import { CsvUploadNoopBanner } from '~/components/common/CsvUpload/CsvUploadNoopBanner'
import { CsvUploadPanelFooter } from '~/components/common/CsvUpload/CsvUploadPanel'
import { CsvUploadStep1Panel } from '~/components/common/CsvUpload/CsvUploadStep1Panel'
import { Dropzone } from '~/components/common/CsvUpload/Dropzone'
import { Stepper } from '~/components/common/CsvUpload/Stepper'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { useImportSupply48Data } from '~/hooks/supply48Data/useImportSupply48Data'
import { useCsvImportReturn } from '~/hooks/useCsvImportReturn'
import { downloadCsv } from '~/libs/downloadCsv'
import { now as serverNow } from '~/models/clock'
import { decodeCsvFile } from '~/models/csvRecords'
import { resolveCsvUploadConfirmGate } from '~/models/csvUploadConfirmGate'
import { formatJstTimeHm } from '~/models/dateTime'
import {
  type ParsedCsvImportRow,
  buildSupply48DataCsvTemplate,
  readSupply48DataCsv,
  toImportRow,
} from '~/models/supply48DataCsv'
import {
  RESULT_TABLE_COLUMN_COUNT,
  RowResultTable,
} from '~/pages/Supply48DataCsvUpload/components/RowResultTable'
import {
  type SummaryCardFilter,
  SummaryCards,
} from '~/pages/Supply48DataCsvUpload/components/SummaryCards'
import {
  SUPPLY48_DATA_CSV_COLUMN_COUNT,
  SUPPLY48_DATA_CSV_TEMPLATE_ROWS,
  supply48DataCsvColumnGuide,
  supply48DataCsvSampleRow,
  supply48DataCsvTemplateFileName,
} from '~/pages/Supply48DataCsvUpload/models/supply48DataCsvColumnGuide'
import { routePaths } from '~/routes/routePaths'

type AppToaster = {
  create: (options: {
    type?: 'success' | 'error' | 'info' | 'loading' | 'warning'
    title?: string
    description?: string
  }) => string
}
const toaster = rawToaster as unknown as AppToaster

type FileInfo = {
  name: string
  size: number
  selectedAt: Date
}

/** バイト数を "38 KB" のような概算表示に変換する。 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/** buySell の表示ラベル (D3: サーバー導出値をそのまま表示、鉄則1)。 */
function buySellLabel(b: Side, t: (key: string) => string): string {
  switch (b) {
    case Side.BUY:
      return t('page:supply48DataCsvUpload.step2.buySell.buy')
    case Side.SELL:
      return t('page:supply48DataCsvUpload.step2.buySell.sell')
    default:
      return t('page:supply48DataCsvUpload.step2.buySell.unspecified')
  }
}

function categoryLabel(c: SupplyCategory, t: (key: string) => string): string {
  switch (c) {
    case SupplyCategory.BILATERAL:
      return t('page:supply48DataCsvUpload.step2.category.bilateral')
    case SupplyCategory.MARKET:
      return t('page:supply48DataCsvUpload.step2.category.market')
    case SupplyCategory.BACKUP_REGULAR:
      return t('page:supply48DataCsvUpload.step2.category.backupRegular')
    case SupplyCategory.FUTURES:
      return t('page:supply48DataCsvUpload.step2.category.futures')
    default:
      return t('page:supply48DataCsvUpload.step2.category.unspecified')
  }
}

/**
 * 48点データ CSV アップロード専用画面
 * (design `admin/uploads/supply-48data.html`)。
 *
 * CSV 取込はこの画面に一本化する (`Supply48Data` グリッド画面のインライン
 * モーダルは撤去済み)。システム管理配下の 2 ステップウィザード
 * (Step1 ファイル選択 → Step2 差分プレビュー → 確定モーダル) として実装する。
 * データフロー (client parse → dry_run プレビュー → 確定) は共有純関数
 * (`~/models/supply48DataCsv`) / 共有 hook
 * (`~/hooks/supply48Data/useImportSupply48Data`) を再利用する。
 */
export const Supply48DataCsvUploadPage = (): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const importMutation = useImportSupply48Data()
  const csvReturn = useCsvImportReturn('supply48Data')

  const [step, setStep] = useState<1 | 2>(1)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [parsedRows, setParsedRows] = useState<ParsedCsvImportRow[]>([])
  // 囲み (ダブルクォート) が正しくない行の物理行番号 (AC-DUC-045)
  const [quoteErrorRow, setQuoteErrorRow] = useState<number | undefined>(
    undefined,
  )
  // UTF-8・Shift_JIS のどちらとしても読めないファイル (common.md §1・§4)
  const [isUndetectableEncoding, setIsUndetectableEncoding] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] =
    useState<ImportSupply48DataResponse | null>(null)
  const [serverErrorMessage, setServerErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [confirmErrorMessage, setConfirmErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [summaryFilter, setSummaryFilter] = useState<SummaryCardFilter>('all')
  // 確定中にファイル選択へ戻った場合、後着応答で新しいCSVを上書きしない。
  const previewGenerationRef = useRef(0)
  // 読込中に別のファイルへ選び直した時、先に選んだファイルの読込結果で上書きしない。
  const readSeqRef = useRef(0)

  const clientErrorRows = parsedRows.filter((r) => r.error !== undefined)
  const hasSelectedFile = fileInfo !== null
  // ヘッダー行だけ・全行が空のファイルは Step1 で止める (AC-DUC-062)。
  const hasNoDataRows =
    hasSelectedFile && quoteErrorRow === undefined && parsedRows.length === 0
  const hasClientError =
    clientErrorRows.length > 0 ||
    quoteErrorRow !== undefined ||
    hasNoDataRows ||
    isUndetectableEncoding

  const handleFileSelected = (file: File): void => {
    previewGenerationRef.current += 1
    setServerErrorMessage(undefined)
    setConfirmErrorMessage(undefined)
    setPreviewResult(null)
    setIsUndetectableEncoding(false)
    const seq = ++readSeqRef.current
    // common.md §1「文字コードの判定」: UTF-8 → Shift_JIS の順で判定し、判定した文字コードで
    // 読んだ値を以降に使う。どちらでも読めない時は §4 の文字コードのエラーで Step1 に止める。
    void decodeCsvFile(file).then((decoded) => {
      if (seq !== readSeqRef.current) return
      if (decoded.kind === 'read') {
        setServerErrorMessage(t('page:supply48DataCsvUpload.serverError'))
        return
      }
      if (decoded.kind === 'encoding') {
        setParsedRows([])
        setQuoteErrorRow(undefined)
        setFileInfo(null)
        setIsUndetectableEncoding(true)
        return
      }
      const result = readSupply48DataCsv(decoded.text)
      setParsedRows(result.rows)
      setQuoteErrorRow(result.quoteErrorRow)
      setFileInfo({
        name: file.name,
        size: file.size,
        selectedAt: serverNow(),
      })
    })
  }

  const runPreview = (): void => {
    setStep(2)
    setIsPreviewLoading(true)
    setServerErrorMessage(undefined)
    setSummaryFilter('all')
    importMutation
      .mutateAsync({ rows: parsedRows.map(toImportRow), dryRun: true })
      .then((resp) => {
        setPreviewResult(resp)
        setIsPreviewLoading(false)
      })
      .catch(() => {
        setServerErrorMessage(t('page:supply48DataCsvUpload.serverError'))
        setIsPreviewLoading(false)
      })
  }

  const handleBackToStep1 = (): void => {
    previewGenerationRef.current += 1
    setStep(1)
    setPreviewResult(null)
  }

  const summary = previewResult?.summary
  const errorRowCount =
    previewResult?.rowResults.filter(
      (r) => r.status === ImportSupply48DataRowStatus.ERROR,
    ).length ?? 0
  const hasRowError = errorRowCount > 0
  // 処理対象は行数で数える — コマ数 (changedKomaCount) は「1 行あたり必ず 1 コマ
  // 以上の差分がある」という backend の暗黙の前提に依存するため使わない。
  const { canOpenConfirm, showNoRowsToProcessBanner } =
    resolveCsvUploadConfirmGate(summary, hasRowError)

  const handleCommit = (): void => {
    const generation = previewGenerationRef.current
    setConfirmErrorMessage(undefined)
    importMutation
      .mutateAsync({
        rows: parsedRows.map(toImportRow),
        dryRun: false,
        // csv_upload_history.csv_file_name に記録される
        // (ADR csv-upload-history-best-effort-recording.md)。
        csvFileName: fileInfo?.name,
      })
      .then((resp) => {
        const isCurrent = generation === previewGenerationRef.current
        if (resp.applied) {
          toaster.create({
            type: 'success',
            title: t('page:supply48DataCsvUpload.successToast'),
          })
          if (isCurrent) {
            setShowConfirmModal(false)
            const applied = resp.summary ?? summary
            csvReturn.returnAfterApply(
              applied === undefined
                ? undefined
                : {
                    created: applied.newRowCount,
                    updated: applied.changedRowCount,
                    deleted: applied.deletedRowCount,
                  },
            )
          }
          return
        }
        if (!isCurrent) return
        setShowConfirmModal(false)
        // D8 相当: 確定時再検証で新たな行エラー → 反映せず Step2 のエラー表示。
        setPreviewResult(resp)
      })
      .catch(() => {
        if (generation !== previewGenerationRef.current) return
        setConfirmErrorMessage(t('page:supply48DataCsvUpload.serverError'))
      })
  }

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:supply48DataCsvUpload.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminUploads'),
          to: routePaths.adminUploads.path,
        },
        {
          label: t('page:supply48DataCsvUpload.breadcrumb.title'),
          bold: true,
        },
      ]}
    >
      {/* spec `admin/data/uploads/index.md:126` :
          「個別アップロード画面のタイトル下補足も本表の説明文と一致させる。 文言変更は
           本 spec の更新をもって行い、 **実装・プロトタイプ側で独自の説明文を持たない**」。
          そのため取込ハブのカードと**同じ i18n キーを共有**する (別キーに複製すると
          片方だけ直って drift する = issue #4325 で禁じられた状態に戻る)。
          見出しは渡さない — パンくず末尾 (bold) が画面名を兼ねる (PageIntro の docstring)。 */}
      <PageIntro description={t('page:adminUploads.types.supply48Data.desc')} />
      {/* 外周 padding は `PageLayout` が包む DS の `SideMenuLayoutContentLayout`
          (`padding="4"` = 16px) が持つ。 ここで更に足すと本画面だけ上・左・右が
          32px になり他画面と揃わない (design `css/layout.css` の
          `.content { padding: 16px }` が正。 デザイナー指摘 2026-08-28)。

          説明文との縦間隔だけは自前で持つ。 `PageIntro` は外側 margin を持たず
          (縦 spacing の所有権を置き場所側へ一本化する方針)、 `PageLayout` も
          children 間に gap を与えないため、 撤去した padding が担っていた 16px が
          消えてステッパーが説明文に接する。 design は説明文側に
          `margin-bottom: var(--space-4)` を置いている (レビュー指摘 2026-08-28)。 */}
      <Box marginTop={4}>
        <Stepper
          currentStep={step}
          stepLabels={[
            t('page:supply48DataCsvUpload.stepper.step1'),
            t('page:supply48DataCsvUpload.stepper.step2'),
          ]}
        />

        {step === 1 && (
          <CsvUploadStep1Panel
            downloads={[
              {
                onClick: () => {
                  const isEnglish = i18n.language.startsWith('en')
                  downloadCsv(
                    supply48DataCsvTemplateFileName(isEnglish),
                    buildSupply48DataCsvTemplate(
                      SUPPLY48_DATA_CSV_TEMPLATE_ROWS,
                      { isEnglish },
                    ),
                  )
                },
              },
            ]}
            // 記入ガイド表・記入例案内は英語表示で英語にする (spec §2、#8673)
            framed
            guideSections={[
              {
                rows: supply48DataCsvColumnGuide(
                  i18n.language.startsWith('en'),
                ),
                columnCount: SUPPLY48_DATA_CSV_COLUMN_COUNT,
              },
            ]}
            sampleRow={supply48DataCsvSampleRow(i18n.language.startsWith('en'))}
            selectHint={t('page:supply48DataCsvUpload.step1.hint')}
          >
            <Dropzone
              onFileSelected={handleFileSelected}
              specText={t('page:supply48DataCsvUpload.step1.spec')}
            />

            {hasClientError && (
              <Alert
                description={
                  <Box>
                    {isUndetectableEncoding && (
                      <Text>{t('common:upload.error.notUtf8')}</Text>
                    )}
                    {quoteErrorRow !== undefined && (
                      <Text>
                        {t('common:upload.error.quoteInvalid', {
                          row: quoteErrorRow,
                        })}
                      </Text>
                    )}
                    {hasNoDataRows && (
                      <Text>{t('common:upload.error.noDataRows')}</Text>
                    )}
                    {clientErrorRows.map((r) => (
                      <Text key={r.rowNumber}>
                        [{r.rowNumber}] {r.error}
                      </Text>
                    ))}
                  </Box>
                }
                status="error"
                title={t('page:supply48DataCsvUpload.step1.syntaxErrorTitle')}
              />
            )}

            {serverErrorMessage !== undefined && (
              <CompactAlert status="error" title={serverErrorMessage} />
            )}

            {hasSelectedFile && fileInfo !== null && (
              // 仕様の行との間は design `.up-file-info` の margin-top 16px (gap 12px + 4px)
              <Box marginTop={1}>
                <CsvUploadFileInfo
                  meta={`${formatFileSize(fileInfo.size)} ・ ${t(
                    'page:supply48DataCsvUpload.fileInfo.selectedAt',
                    { time: formatJstTimeHm(fileInfo.selectedAt) },
                  )}`}
                  name={fileInfo.name}
                />
              </Box>
            )}

            <CsvUploadPanelFooter>
              <TertiaryButton
                onClick={() => {
                  void navigate(csvReturn.cancelPath)
                }}
                size="md"
              >
                {t('page:supply48DataCsvUpload.step1.cancel')}
              </TertiaryButton>
              {hasSelectedFile && !hasClientError && (
                <PrimaryButton onClick={runPreview} size="md">
                  {t('page:supply48DataCsvUpload.step1.toStep2')}
                </PrimaryButton>
              )}
            </CsvUploadPanelFooter>
          </CsvUploadStep1Panel>
        )}

        {step === 2 && (
          <CsvDiffPanel
            back={{
              label: t('page:supply48DataCsvUpload.step2.backToStep1'),
              onClick: handleBackToStep1,
              disabled: csvReturn.isReturning,
            }}
            confirm={{
              label: t('page:supply48DataCsvUpload.step2.confirmOpen'),
              onClick: () => setShowConfirmModal(true),
              // 業務画面へ戻るのを待つ間に、 同じ CSV をもう一度確定させない
              disabled: !canOpenConfirm || csvReturn.isReturning,
            }}
            hint={t('page:supply48DataCsvUpload.step2.hint')}
            title={t('page:supply48DataCsvUpload.step2.title')}
          >
            {/* AC-DUC-005: Step2 にもアップロード済みファイル情報を常時表示する */}
            {fileInfo !== null && (
              <Box marginBottom={3}>
                <CsvUploadFileInfo
                  meta={`${formatFileSize(fileInfo.size)} ・ ${t(
                    'page:supply48DataCsvUpload.fileInfo.selectedAt',
                    { time: formatJstTimeHm(fileInfo.selectedAt) },
                  )}`}
                  name={fileInfo.name}
                />
              </Box>
            )}

            {isPreviewLoading && (
              <CsvDiffLoading
                columnCount={RESULT_TABLE_COLUMN_COUNT}
                label={t('page:supply48DataCsvUpload.step2.loading')}
              />
            )}

            {serverErrorMessage !== undefined && (
              <Box marginBottom={3}>
                <CompactAlert status="error" title={serverErrorMessage} />
              </Box>
            )}

            {!isPreviewLoading && hasRowError && (
              <Box marginBottom={3}>
                <CompactAlert
                  status="error"
                  title={t('page:supply48DataCsvUpload.step2.errorBannerDesc', {
                    count: errorRowCount,
                  })}
                />
              </Box>
            )}

            {!isPreviewLoading &&
              showNoRowsToProcessBanner &&
              summary !== undefined && (
                <CsvUploadNoopBanner
                  description={t(
                    'page:supply48DataCsvUpload.step2.noopBannerDesc',
                    { count: summary.unchangedRowCount },
                  )}
                />
              )}

            {!isPreviewLoading &&
              previewResult !== null &&
              summary !== undefined && (
                <>
                  <SummaryCards
                    activeFilter={summaryFilter}
                    onFilterChange={setSummaryFilter}
                    summary={summary}
                  />
                  <HStack gap={4} marginBottom={3}>
                    <Text fontSize="sm">
                      {t(
                        'page:supply48DataCsvUpload.confirmModal.changedKomaCount',
                        { count: summary.changedKomaCount },
                      )}
                    </Text>
                    <Text fontSize="sm">
                      {t(
                        'page:supply48DataCsvUpload.confirmModal.skippedKomaCount',
                        { count: summary.skippedKomaCount },
                      )}
                    </Text>
                  </HStack>
                  <RowResultTable
                    filter={summaryFilter}
                    parsedRows={parsedRows}
                    rowResults={previewResult.rowResults}
                  />
                </>
              )}
          </CsvDiffPanel>
        )}

        <ConfirmDialog
          body={
            <Box>
              {confirmErrorMessage !== undefined && (
                <Alert
                  marginBottom={3}
                  status="error"
                  title={confirmErrorMessage}
                />
              )}
              <Text marginBottom={2}>
                {t('page:supply48DataCsvUpload.confirmModal.body')}
              </Text>
              {summary !== undefined && summary.warningRowCount > 0 && (
                // AC-S4D-045: 時間帯区分整合の警告は「確認ダイアログで表示し、
                // 確認の上で保存を許可する」が要件。 専用ダイアログは新設せず、
                // 既存の「アップロードを確定」ダイアログ自体をその確認ダイアログ
                // として機能させる (プレビュー表の備考セルだけでは 5,000 行の
                // ファイルで埋没し、確認として成立しないため)。
                <Alert
                  marginBottom={2}
                  status="warning"
                  title={t(
                    'page:supply48DataCsvUpload.confirmModal.warningRowCount',
                    { count: summary.warningRowCount },
                  )}
                />
              )}
              {summary !== undefined && (
                <Box
                  marginBottom={2}
                  padding={2}
                  borderWidth="1px"
                  borderColor="Border.lowEmphasis"
                  borderRadius="md"
                >
                  <Text fontSize="sm">
                    {t('page:supply48DataCsvUpload.confirmModal.newRowCount', {
                      count: summary.newRowCount,
                    })}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:supply48DataCsvUpload.confirmModal.changedRowCount',
                      { count: summary.changedRowCount },
                    )}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:supply48DataCsvUpload.confirmModal.unchangedRowCount',
                      { count: summary.unchangedRowCount },
                    )}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:supply48DataCsvUpload.confirmModal.deletedRowCount',
                      { count: summary.deletedRowCount },
                    )}
                  </Text>
                  <Text
                    textStyle="captionStrong"
                    marginTop={1}
                    paddingTop={1}
                    borderTopWidth="1px"
                    borderTopColor="Border.lowEmphasis"
                  >
                    {t(
                      'page:supply48DataCsvUpload.confirmModal.processedTotal',
                      {
                        count:
                          summary.newRowCount +
                          summary.changedRowCount +
                          summary.unchangedRowCount +
                          summary.deletedRowCount,
                      },
                    )}
                  </Text>
                </Box>
              )}
              {summary !== undefined && summary.breakdowns.length > 0 && (
                <Box marginBottom={2}>
                  <Text textStyle="captionStrong">
                    {t(
                      'page:supply48DataCsvUpload.confirmModal.breakdownTitle',
                    )}
                  </Text>
                  {summary.breakdowns.map((b, i) => (
                    <Text key={i} fontSize="sm">
                      {buySellLabel(b.buySell, t)} /{' '}
                      {categoryLabel(b.category, t)}: {b.rowCount}
                    </Text>
                  ))}
                </Box>
              )}
              <Text fontSize="sm">
                {t('page:supply48DataCsvUpload.confirmModal.changedKomaCount', {
                  count: summary?.changedKomaCount ?? 0,
                })}
              </Text>
              <Text fontSize="sm">
                {t('page:supply48DataCsvUpload.confirmModal.skippedKomaCount', {
                  count: summary?.skippedKomaCount ?? 0,
                })}
              </Text>
            </Box>
          }
          confirmLabel={t('page:supply48DataCsvUpload.confirmModal.commit')}
          isLoading={importMutation.isPending}
          isOpen={showConfirmModal}
          onCancel={() => {
            setConfirmErrorMessage(undefined)
            setShowConfirmModal(false)
          }}
          onConfirm={handleCommit}
          title={t('page:supply48DataCsvUpload.confirmModal.title')}
        />
      </Box>
    </PageLayout>
  )
}
